import {
  ReviewLifecycleTerminalState,
  ReviewLifecycleType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import * as Perf from 'performance/Perf';
import {SearchResponse} from 'ui-library/entity-input/EntityInputWithSearch';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {ReviewLifecycleStage, ReviewLifecycleVersion} from './ReviewLifecycleConstants';

export function reviewLifecyclesListingURL(): string {
  return `/entry-review-processes`;
}

export function configureReviewLifecycleURL(): string {
  return `/entry-review-processes/configuration`;
}

export function reviewLifecycleURL(reviewLifecycleId: string): string {
  return `/entry-review-processes/${reviewLifecycleId}`;
}

export interface ReviewLifecycleResponse {
  id: string;
  reviewLifecycleVersion: ReviewLifecycleVersion;
}

interface BaseReviewLifecycleActionSpec {
  name: string;
  lifecycleType: ReviewLifecycleType;
  terminalState: ReviewLifecycleTerminalState;
}

interface CreateReviewLifecycleSpec extends BaseReviewLifecycleActionSpec {
  reviewStages: Array<Omit<ReviewLifecycleStage, 'previousVersionReviewLifecycleStageId'>>;
}

interface UpdateReviewLifecycleSpec extends BaseReviewLifecycleActionSpec {
  reviewStages: Array<ReviewLifecycleStage>;
}

export function createReviewLifecycle({
  organizationId,
  lifecycleType,
  name,
  terminalState,
  reviewStages,
}: {
  organizationId: string;
} & CreateReviewLifecycleSpec): Promise<ReviewLifecycleResponse> {
  const reviewLifecycleVersionSpec: CreateReviewLifecycleSpec = {
    name,
    lifecycleType,
    reviewStages,
    terminalState,
  };

  return net.postJSONPromise({
    url: '/1/api/review-lifecycles',
    data: {
      reviewLifecycleVersionSpec,
      organizationId,
    },
  });
}

export async function updateReviewLifecycle({
  reviewLifecycleId,
  lifecycleType,
  name,
  terminalState,
  reviewStages,
}: {
  reviewLifecycleId: string;
} & UpdateReviewLifecycleSpec): Promise<ReviewLifecycleResponse> {
  let success = false;
  const perfId = Perf.start('update_review_lifecycle', {enabled: true, getMetadata: () => ({success})}) ?? '';
  Perf.markStart(perfId);

  const reviewLifecycleVersionSpec: UpdateReviewLifecycleSpec = {
    name,
    lifecycleType,
    reviewStages,
    terminalState,
  };
  const {response} = await TaskRunner.patchJSONPromise({
    url: `/1/api/review-lifecycles/${reviewLifecycleId}`,
    data: {reviewLifecycleVersionSpec},
  }).then((response) => {
    success = true;
    return response;
  });

  Perf.markEnd(perfId);
  Perf.stop(perfId);

  notifications.success(
    'Review lifecycle has successfully updated. It might take up to a few minutes for the changes to reflect in search.'
  );

  return response;
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkArchiveReviewLifecycles(
  ids: Array<string>,
  purpose: string
): Promise<BulkArchiveResponse> {
  return net.postJSONPromise({
    url: `/1/api/review-lifecycles/archive`,
    data: {reviewLifecycleIds: ids, reason: purpose},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchiveReviewLifecycles(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return net.postJSONPromise({
    url: `/1/api/review-lifecycles/unarchive`,
    data: {reviewLifecycleIds: ids},
  });
}

export function searchForReviewers(
  reviewableId: string,
  query: string,
  teamIds?: Array<string>
): Promise<SearchResponse> {
  return net.postJSONPromise({
    url: '/1/api/rlc-reviews/collaborator-search',
    data: {reviewableId, query, teamApiIds: teamIds},
  });
}
